import React, { useContext } from "react"
import { useLocalStorage } from "../hooks/useLocalStorage"
import { useAsyncInterval } from "../hooks/useInterval"

export const CartsContext = React.createContext<ICartContext | null>(null)

export type ICartProduct = {
  id: string
  quantity: number
}

interface ICartContext {
  productMasters: ICartProduct[]

  addProductMaster(id: string, quantity: number): void

  setCart(productMasters: ICartProduct[]): void

  removeProductMaster(id: string): void

  changeProductMasterQty(id: string, quantity: number): void

  clearCart(): void
}

import { useIsBranchGuest } from "../hooks/useBranchGuestSession"

export function CartProvider({ children }: { children: React.ReactNode }) {
  const skeem = window.skeem
  const isGuest = useIsBranchGuest()
  const [productMasters, setProductMasters] = useLocalStorage<ICartProduct[]>("cart", [])

  // Once a minute, check that all products in the cart actually exist. This is to clear out possible
  // deleted products or other data issues that lead to products not existing, resulting in errors on
  // the job form.
  useAsyncInterval(validateProductsExist, 1000 * 60)
  async function validateProductsExist() {
    const products = await skeem.fetch2<{ id: string }[]>({
      model: {
        name: "product_masters",
        filter: { matches: { id: productMasters.map(p => p.id) } },
      },
    })
    if (products.length !== productMasters.length) {
      setProductMasters(pms => pms.filter(pm => !!products.find(v => v.id === pm.id)))
    }
  }

  function addProductMaster(id: string, quantity: number) {
    // Branch staff in a guest session can manage favourites, but not build a basket
    if (isGuest) return
    // check if product already exists
    const product = productMasters.find(p => p.id === id)
    if (product) {
      const newQuantity = product.quantity + quantity
      setProductMasters(pms => pms.map(p => (p.id === id ? { ...p, quantity: newQuantity } : p)))
    } else {
      setProductMasters(pms => [...pms, { id, quantity }])
    }
  }

  function setCart(productMasters: ICartProduct[]) {
    if (isGuest) return
    setProductMasters(productMasters)
  }

  function removeProductMaster(id: string) {
    if (isGuest) return
    setProductMasters(pms => pms.filter(p => p.id !== id))
  }

  function changeProductMasterQty(id: string, quantity: number) {
    if (isGuest) return
    setProductMasters(pms => pms.map(p => (p.id === id ? { ...p, quantity } : p)))
  }

  function clearCart() {
    if (isGuest) return
    setProductMasters([])
  }

  return (
    <CartsContext.Provider
      value={{
        addProductMaster,
        removeProductMaster,
        changeProductMasterQty,
        productMasters,
        clearCart,
        setCart,
      }}
    >
      {children}
    </CartsContext.Provider>
  )
}

export function useCart() {
  const value = useContext(CartsContext)
  if (!value) {
    throw new Error("CartsProvider must be used within a CartsContext.")
  }
  return value
}
